import { useEffect, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RequirePermission, PermissionDenied } from '@/components/RequirePermission';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage, useT, type TranslateFn } from '@/i18n/LanguageContext';
import { formatCurrencyMinor, formatDate as formatDateIntl, formatDateTime, formatNumber } from '@/i18n/format';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { SubscriptionView, SubscriptionUsage, SubscriptionPlanOption } from '@/api/types';

/**
 * ADR-077 Part E — the SaaS subscription screen.
 *
 * ZERO BUSINESS LOGIC LIVES HERE, and that is worth being explicit about
 * because it would be so easy to add some. This page never computes whether a
 * metric is exhausted, whether a capability is blocked, whether a change needs
 * approval, or what a status means for what the operator may do. Every one of
 * those is already decided by the server and arrives as a finished field
 * (`exhausted`, `blockedCapabilities`, `requiresApproval`, `readOnly`) from
 * apps/api/src/routes/subscriptionView.ts — the same serialiser the platform
 * console reads, so the two can never disagree about a customer's state.
 *
 * What IS here: labels, ordering, and formatting. That is the whole job.
 */

const CHANGE_KIND_KEYS: Record<string, TranslationKey> = {
  UPGRADE: 'subscription.changeKind.UPGRADE',
  DOWNGRADE: 'subscription.changeKind.DOWNGRADE',
};

const INTERVAL_KEYS: Record<string, TranslationKey> = {
  MONTH: 'subscription.interval.MONTH',
  YEAR: 'subscription.interval.YEAR',
};

function changeKindLabel(t: TranslateFn, raw: string): string {
  const key = CHANGE_KIND_KEYS[raw];
  return key ? t(key) : raw.toLowerCase();
}

function intervalLabel(t: TranslateFn, raw: string): string {
  const key = INTERVAL_KEYS[raw];
  return key ? t(key) : raw.toLowerCase();
}

const METRIC_LABEL_KEYS: Record<string, TranslationKey> = {
  WAREHOUSES: 'subscription.metricWarehouses',
  INTERNAL_USERS: 'subscription.metricInternalUsers',
  SELLER_PORTAL_USERS: 'subscription.metricSellerPortalUsers',
  MARKETPLACE_CONNECTIONS: 'subscription.metricMarketplaceConnections',
  SELLERS: 'subscription.metricSellers',
  EVIDENCE_STORAGE_BYTES: 'subscription.metricEvidenceStorage',
  RETENTION_DAYS: 'subscription.metricEvidenceRetention',
};

const CAPABILITY_LABEL_KEYS: Record<string, TranslationKey> = {
  FBS: 'subscription.capFbs',
  FBO: 'subscription.capFbo',
  RETURNS: 'subscription.capReturns',
  MULTI_WAREHOUSE: 'subscription.capMultiWarehouse',
  MULTI_LEGAL_ENTITY: 'subscription.capMultiLegalEntity',
  ADVANCED_BILLING: 'subscription.capAdvancedBilling',
  SELLER_PORTAL: 'subscription.capSellerPortal',
  PUBLIC_API: 'subscription.capPublicApi',
  WEBHOOKS: 'subscription.capWebhooks',
  DATA_EXPORT: 'subscription.capDataExport',
  MIGRATION_WIZARD: 'subscription.capMigrationWizard',
  RU_CHESTNY_ZNAK: 'subscription.capChestnyZnak',
  RU_EDO: 'subscription.capEdo',
  ADVANCED_REPORTS: 'subscription.capAdvancedReports',
  VIDEO_EVIDENCE: 'subscription.capVideoEvidence',
};

function metricLabel(t: TranslateFn, key: string): string {
  const k = METRIC_LABEL_KEYS[key];
  return k ? t(k) : key;
}

function capabilityLabel(t: TranslateFn, key: string): string {
  const k = CAPABILITY_LABEL_KEYS[key];
  return k ? t(k) : key;
}

function formatValue(t: TranslateFn, locale: 'en' | 'ru', metric: string, value: number | null): string {
  if (value === null) return t('subscription.unlimited');
  if (metric === 'EVIDENCE_STORAGE_BYTES') {
    if (value >= 1_073_741_824) return `${formatNumber(value / 1_073_741_824, locale, { maximumFractionDigits: 1 })} GB`;
    if (value >= 1_048_576) return `${formatNumber(value / 1_048_576, locale, { maximumFractionDigits: 1 })} MB`;
    return `${formatNumber(value, locale)} B`;
  }
  if (metric === 'RETENTION_DAYS') return t('subscription.nDays', { n: value });
  return formatNumber(value, locale);
}

function formatDate(iso: string | null, locale: 'en' | 'ru'): string {
  return iso === null ? '—' : formatDateIntl(iso, locale);
}

function formatPrice(minor: string | null, currency: string | null, locale: 'en' | 'ru'): string {
  if (minor === null || currency === null) return '—';
  return formatCurrencyMinor(minor, currency, locale);
}

/**
 * The status banner. Deliberately reads `readOnly` rather than switching on
 * the status string: a future status that is read-only needs no change here.
 */
function StatusBanner({ view }: { view: SubscriptionView }): JSX.Element {
  const { t, locale } = useLanguage();
  if (view.effectiveStatus === 'UNMANAGED') {
    return (
      <div className="card" data-testid="subscription-status">
        <h3>{t('subscription.noSubscriptionOnFile')}</h3>
        <p className="sub">{t('subscription.noSubscriptionHint')}</p>
      </div>
    );
  }
  if (view.readOnly) {
    return (
      <div className="card" data-testid="subscription-status">
        <h3>
          <span className="badge danger">{view.effectiveStatus}</span> {t('subscription.readOnlyTitle')}
        </h3>
        <p className="sub">
          {view.suspendReason ? t('subscription.reasonPrefix', { reason: view.suspendReason }) : ''}
          {t('subscription.readOnlyHint', { status: view.effectiveStatus.toLowerCase() })}
        </p>
      </div>
    );
  }
  if (view.effectiveStatus === 'PAST_DUE') {
    return (
      <div className="card" data-testid="subscription-status">
        <h3>
          <span className="badge warn">{t('subscription.pastDueBadge')}</span> {t('subscription.paymentOutstanding')}
        </h3>
        <p className="sub">{t('subscription.pastDueHint', { date: formatDate(view.graceEndsAt, locale) })}</p>
      </div>
    );
  }
  if (view.effectiveStatus === 'TRIAL') {
    return (
      <div className="card" data-testid="subscription-status">
        <h3>
          <span className="badge neutral">{t('subscription.trialBadge')}</span> {t('subscription.trialEnds', { date: formatDate(view.trialEndsAt, locale) })}
        </h3>
        <p className="sub">{t('subscription.trialHint', { plan: view.plan?.displayName ?? t('subscription.currentPlanFallback') })}</p>
      </div>
    );
  }
  if (view.effectiveStatus === 'CANCELLED') {
    return (
      <div className="card" data-testid="subscription-status">
        <h3>
          <span className="badge warn">{t('subscription.cancelledBadge')}</span> {t('subscription.ends', { date: formatDate(view.currentPeriodEnd, locale) })}
        </h3>
        <p className="sub">{t('subscription.cancelledHint')}</p>
      </div>
    );
  }
  return (
    <div className="card" data-testid="subscription-status">
      <h3>
        <span className="badge success">{t('subscription.activeBadge')}</span> {view.plan?.displayName ?? t('subscription.currentPlanFallback')}
      </h3>
      <p className="sub">{t('subscription.renews', { date: formatDate(view.currentPeriodEnd, locale) })}</p>
    </div>
  );
}

function UsageTable({ usage }: { usage: readonly SubscriptionUsage[] }): JSX.Element {
  const { t, locale } = useLanguage();
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>{t('subscription.usageAndLimits')}</h3>
      <div className="table-scroll"><table data-testid="subscription-usage">
        <thead>
          <tr>
            <th>{t('subscription.colResource')}</th>
            <th>{t('subscription.colInUse')}</th>
            <th>{t('subscription.colIncluded')}</th>
            <th>{t('common.status')}</th>
          </tr>
        </thead>
        <tbody>
          {usage.map((u) => (
            <tr key={u.metric} data-testid={`usage-${u.metric}`}>
              <td>
                {metricLabel(t, u.metric)}
                {u.source === 'OVERRIDE' && <span className="badge neutral" style={{ marginLeft: 8 }}>{t('subscription.adjusted')}</span>}
              </td>
              <td>{u.enforced ? formatValue(t, locale, u.metric, u.used) : '—'}</td>
              <td>{formatValue(t, locale, u.metric, u.limit)}</td>
              <td>
                {!u.enforced ? (
                  <span className="sub">{t('subscription.notALimit')}</span>
                ) : u.exhausted ? (
                  <span className="badge danger">{t('subscription.limitReached')}</span>
                ) : u.remaining === null ? (
                  <span className="badge success">{t('subscription.unlimited')}</span>
                ) : (
                  <span className="sub">{t('subscription.nLeft', { n: formatNumber(u.remaining, locale) })}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

function Capabilities({ view }: { view: SubscriptionView }): JSX.Element {
  const t = useT();
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>{t('subscription.includedFeatures')}</h3>
      <ul data-testid="subscription-capabilities">
        {view.capabilities.map((c) => (
          <li key={c}>{capabilityLabel(t, c)}</li>
        ))}
        {view.capabilities.length === 0 && view.blockedCapabilities.length === 0 && (
          <li className="sub">{t('subscription.noFeaturesListed')}</li>
        )}
      </ul>
      {view.blockedCapabilities.length > 0 && (
        <>
          {/*
            Blocked is not the same as "not in your plan" - these are features
            this organisation BOUGHT and that its current subscription status
            is withholding. Showing them separately is what lets the operator
            see that the remedy is to restore the subscription, not to upgrade.
          */}
          <h4 style={{ marginTop: 12 }}>{t('subscription.currentlyBlocked')}</h4>
          <ul data-testid="subscription-blocked">
            {view.blockedCapabilities.map((c) => (
              <li key={c}>
                <span className="badge danger">{t('status.blocked')}</span> {capabilityLabel(t, c)}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function PlanChange({
  view,
  plans,
  onChanged,
  notice,
  setNotice,
}: {
  view: SubscriptionView;
  plans: readonly SubscriptionPlanOption[];
  onChanged: () => void;
  /**
   * Lifted OUT of this component on purpose. A reload re-runs the page's
   * fetch, and the confirmation message must survive that - keeping it in
   * local state meant the reload wiped the very message it had just earned,
   * so the operator clicked "Request change", saw a flicker, and was told
   * nothing. Caught by the Playwright spec, not by inspection.
   */
  notice: string | null;
  setNotice: (value: string | null) => void;
}): JSX.Element {
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const { t, locale } = useLanguage();

  const selectable = plans.filter((p) => p.currentVersion !== null && p.currentVersion.planVersionId !== view.subscriptionPlanVersionId);

  async function run(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>{t('subscription.changePlan')}</h3>
      {Boolean(error) && <ErrorBanner error={error} onRetry={() => setError(null)} />}
      {notice !== null && (
        <div className="empty-state" role="status" data-testid="plan-change-notice">
          {notice}
        </div>
      )}

      {view.scheduledChange !== null ? (
        <div data-testid="scheduled-change">
          <p>
            {t('subscription.scheduledChangeText', {
              plan: view.scheduledChange.planDisplayName,
              kind: changeKindLabel(t, view.scheduledChange.kind),
              date: formatDate(view.scheduledChange.effectiveAt, locale),
            })}
          </p>
          {view.scheduledChange.requiresApproval && (
            <p className="sub" data-testid="awaiting-approval">
              {t('subscription.waitingForApproval')}
            </p>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              run(async () => {
                await endpoints.cancelSubscriptionPlanChange();
                setNotice(t('subscription.scheduledChangeWithdrawn'));
              })
            }
          >
            {t('subscription.withdrawChange')}
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (target === '') return;
            void run(async () => {
              const result = await endpoints.requestSubscriptionPlanChange({ planVersionId: target });
              setNotice(
                result.requiresApproval
                  ? t('subscription.upgradeRequestedNotice', { date: formatDate(result.effectiveAt, locale) })
                  : t('subscription.changeScheduledNotice', { date: formatDate(result.effectiveAt, locale) }),
              );
              setTarget('');
            });
          }}
        >
          <label htmlFor="plan-target">{t('subscription.moveTo')}</label>
          <select id="plan-target" value={target} onChange={(e) => setTarget(e.target.value)} disabled={busy}>
            <option value="">{t('subscription.selectPlan')}</option>
            {selectable.map((p) => (
              <option key={p.planId} value={p.currentVersion!.planVersionId}>
                {p.displayName} — {formatPrice(p.currentVersion!.basePriceMinor, p.currentVersion!.currency, locale)} /{' '}
                {intervalLabel(t, p.billingInterval)}
              </option>
            ))}
          </select>
          <button type="submit" disabled={busy || target === ''}>
            {t('subscription.requestChange')}
          </button>
          <p className="sub">{t('subscription.requestChangeHint')}</p>
        </form>
      )}

      <hr />

      {view.cancelAtPeriodEnd ? (
        <button
          type="button"
          disabled={busy}
          data-testid="resume-subscription"
          onClick={() =>
            run(async () => {
              await endpoints.resumeSubscription();
              setNotice(t('subscription.cancellationWithdrawn'));
            })
          }
        >
          {t('subscription.keepMySubscription')}
        </button>
      ) : (
        <button
          type="button"
          disabled={busy || view.effectiveStatus === 'UNMANAGED'}
          data-testid="cancel-subscription"
          onClick={() =>
            run(async () => {
              await endpoints.cancelSubscription({});
              setNotice(t('subscription.cancelledNotice'));
            })
          }
        >
          {t('subscription.cancelAtPeriodEnd')}
        </button>
      )}
    </div>
  );
}

export function SubscriptionPage(): JSX.Element {
  const { hasPermission } = useAuth();
  const result = useApi(() => endpoints.subscription(), []);
  const [notice, setNotice] = useState<string | null>(null);
  const { t, locale } = useLanguage();
  /**
   * The last view that loaded successfully. A reload after a write must not
   * blank the whole screen back to a spinner - the operator has just acted and
   * needs to see the result of that action next to the data it changed, not a
   * full-page flash. (Repo anti-pattern list: no full-page spinner when
   * partial data is available.)
   */
  const [lastGood, setLastGood] = useState<SubscriptionView | null>(null);
  useEffect(() => {
    if (result.status === 'success') setLastGood(result.data);
  }, [result.status, result.data]);

  if (!hasPermission('SUBSCRIPTION.READ')) {
    return <PermissionDenied reason={t('subscription.permissionDenied')} />;
  }
  const view = result.status === 'success' ? result.data : lastGood;
  if (view === null) {
    if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
    return <LoadingState label={t('subscription.loading')} />;
  }

  return (
    <div>
      <h2>{t('nav.subscription')}</h2>
      <StatusBanner view={view} />

      {view.plan !== null && (
        <div className="card" style={{ marginTop: 16 }} data-testid="subscription-plan">
          <h3>{view.plan.displayName}</h3>
          <p className="sub">
            {t('subscription.planSummary', {
              price: formatPrice(view.basePriceMinor, view.currency, locale),
              interval: intervalLabel(t, view.plan.billingInterval),
              version: view.planVersion === null ? '—' : formatNumber(view.planVersion, locale),
              from: formatDate(view.currentPeriodStart, locale),
              to: formatDate(view.currentPeriodEnd, locale),
            })}
          </p>
        </div>
      )}

      <UsageTable usage={view.usage} />
      <Capabilities view={view} />

      <RequirePermission permission="SUBSCRIPTION.WRITE">
        <PlanChange
          view={view}
          plans={view.availablePlans}
          onChanged={result.reload}
          notice={notice}
          setNotice={setNotice}
        />
      </RequirePermission>

      {view.history.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>{t('subscription.history')}</h3>
          <div className="table-scroll"><table data-testid="subscription-history">
            <thead>
              <tr>
                <th>{t('subscription.colWhen')}</th>
                <th>{t('subscription.colChange')}</th>
                <th>{t('subscription.colBy')}</th>
                <th>{t('common.reason')}</th>
              </tr>
            </thead>
            <tbody>
              {view.history.map((h) => (
                <tr key={h.id}>
                  <td>{formatDateTime(h.createdAt, locale)}</td>
                  <td>{h.changeKind.replaceAll('_', ' ').toLowerCase()}</td>
                  <td>{h.actorKind.replaceAll('_', ' ').toLowerCase()}</td>
                  <td>{h.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}
